import { identity } from "lodash";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useCallback
} from "react";
import { usePrevious } from "./use-changed";

/**
 * Provides a hook to read and store arbitrary data as JSON strings in the localStorage
 * Automatically syncs the storage, to reflect changes to the storage item from other tabs.
 * @param key The local storage key
 * @param defaultValue A default value in case the storage item is undefined or malformed json
 * @example const [item, setItem] = useLocalStorage("my.storage.key", {someValue: "foo"})
 * console.log(localStorage.getItem("my.storage.key")) // => '{ "someValue": "foo" }'
 * console.log(item.someValue) // => "foo"
 */
function useLocalStorage<T>(
  key: string,
  defaultValue: T,
  options: {
    deserializer?: (val: any) => T;
    onSync?: (val: T) => void;
    disabled?: boolean;
  } = {}
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const { disabled, deserializer, onSync } = {
    deserializer: identity,
    ...options
  } as Required<Pick<typeof options, "deserializer">> &
    Omit<typeof options, "deserializer">;

  const [value, setValue] = useState<T>(
    disabled ? defaultValue : loadValue(key, defaultValue, deserializer)
  );

  const prevValue = usePrevious(value);

  useEffect(() => {
    if (disabled || prevValue === value) {
      return;
    }
    localStorage.setItem(key, JSON.stringify(value));
  }, [prevValue, value, key, disabled]);

  useEffect(() => {
    setValue(value => {
      const nextValue = disabled
        ? defaultValue
        : loadValue(key, defaultValue, deserializer);
      return value || nextValue;
    });
  }, [key, defaultValue, disabled]);

  useEffect(() => {
    function sync(ev: StorageEvent) {
      if (key !== ev.key) {
        return;
      }

      onSync?.(
        deserializer(ev.newValue === null ? [] : JSON.parse(ev.newValue)) as T
      );
    }
    window.addEventListener("storage", sync);

    return () => window.removeEventListener("storage", sync);
  }, [key]);

  const clearValue = useCallback(() => localStorage.removeItem(key), [key]);

  return [value, setValue, clearValue];
}

function loadValue<T>(
  key: string,
  defaultValue: T,
  deserializer: (val: any) => T
) {
  const item = localStorage.getItem(key);
  try {
    const value =
      typeof item !== "string"
        ? defaultValue
        : (deserializer(JSON.parse(item)) as T);

    return value;
  } catch (err) {
    // tslint:disable-next-line: no-console
    console.warn("found a corrupted localStorage value: '%s'", item);
    return defaultValue;
  }
}

export default useLocalStorage;
